/**
 * On touch screens, swiping scrolls natively even when it starts on a word,
 * while a tap, long press or sideways drag acts on the word. A touch on a word
 * is pending until it turns out to be one or the other.
 */

const longPressMilliseconds = 450;
/** How far a touch may move and still count as a press rather than a swipe */
const moveTolerancePixels = 10;
/** A touch this soon after scrolling stops the scroll, rather than tapping */
const scrollStopMilliseconds = 150;

let lastScrollTime = 0;
if (typeof window !== "undefined") {
  window.addEventListener("scroll", () => (lastScrollTime = Date.now()), {
    capture: true,
    passive: true,
  });
}

/** Whether a touch starting now stops a scroll, so mustn't act on a word */
export const stopsScroll = () =>
  Date.now() - lastScrollTime < scrollStopMilliseconds;

export class PendingTouch {
  private settled = false;
  private timer: number;

  /** `onHold` on a long press or sideways drag, which selects a phrase */
  constructor(
    private start: { clientX: number; clientY: number },
    private onHold: () => void
  ) {
    this.timer = window.setTimeout(this.hold, longPressMilliseconds);
  }

  private hold = () => {
    this.cancel();
    this.onHold();
  };

  move(event: TouchEvent) {
    if (this.settled) {
      return;
    }
    const deltaX = event.touches[0].clientX - this.start.clientX;
    const deltaY = event.touches[0].clientY - this.start.clientY;
    if (event.touches.length > 1) {
      this.cancel();
    } else if (Math.hypot(deltaX, deltaY) > moveTolerancePixels) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this.hold(); // A sideways drag
      } else {
        this.cancel(); // A swipe, which the browser scrolls
      }
    }
  }

  /** On the touch ending: whether it was a tap */
  end() {
    const tap = !this.settled;
    this.cancel();
    return tap;
  }

  cancel() {
    this.settled = true;
    clearTimeout(this.timer);
  }
}
